<template>
  <v-container fluid class='ma-0 pa-0'>

    <loading
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />   

    <!-- Dialog -->
    <v-dialog v-model="visible" :max-width="width" persistent>
      <v-card>
        <v-toolbar dark flat :color="appColor_Resident" dense>
          <v-toolbar-title>
            <v-icon class="mr-2">person</v-icon>{{ formTitle }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-0 pt-2 ma-0">
          <v-container grid-list-md class="pt-0 pb-0">
            <form>
              <v-row v-if="editedResident.id > 0" no-gutters>
                <v-list-item class="pa-0 mb-2">
                  <v-list-item-avatar tile size="90" class="pa-0">
                    <v-img :src="picture"></v-img>
                  </v-list-item-avatar>

                  <v-spacer />

                  <v-list-item-icon class="mt-2 mr-1">
                    <v-btn @click="changePicture()" dark :color="appColor_ResidentDark" class="rounded-0 body-2">{{ changePictureText }}</v-btn>
                    <input v-show="false" type="file" accept='image/*' ref="picture" @change="uploadPicture()">
                  </v-list-item-icon>
                </v-list-item>              
              </v-row>

              <!-- Row 1 (First Name) -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedResident.firstName"
                    :label="getExpression('Label', 'FirstName')"
                    v-validate="'required|max:100'"
                    :error-messages="errors.collect('firstName')"
                    data-vv-name="firstName"
                    ref="firstName"
                    >
                  </v-text-field>
                </v-col>
              </v-row>
              
              <!-- Row 2 (Last Name) -->
              <v-row no-gutters>
                <v-col cols="12" sm="12">
                  <v-text-field
                    v-model="editedResident.lastName"
                    :label="getExpression('Label', 'LastName')"
                    class="mb-2"
                  />
                </v-col>
              </v-row>

              <!-- Row 3 (Language/Key #) -->
              <v-row no-gutters>
                <v-col cols="12" :sm="isResidentManager ? '12' : '7'">
                  <v-select
                    v-model="editedResident.languageId"
                    :items="languagedLanguages"
                    item-value="id"
                    item-text="description"
                    :label="getExpression('Label', 'Language')"
                    v-validate="'required'"
                    :error-messages="errors.collect('language')"
                    data-vv-name="language"
                  />
                </v-col>
                <template v-if="!isResidentManager">
                  <v-col cols="12" sm="5">

                    <!-- if any keyNums show the dropdown -->
                    <v-select
                      v-if="keyNums.length > 1"
                      v-model="editedResident.kioskKeyTagId"
                      :items="keyNums"
                      item-value="id"
                      item-text="keyNum"
                      class="ml-2"
                      >
                      <template #label>
                        <label v-html="getExpression('Label', 'KeyId')"></label>
                      </template>
                    </v-select>
                    <!-- if no keyNums then show bluetooth beacon textbox -->
                    <v-text-field
                      v-else
                      v-model="editedResident.bluetoothBeaconId"
                        type="number"
                        v-validate="isAbby ? '' : 'required'"
                        :error-messages="errors.collect('keyId')"
                        data-vv-name="keyId"
                        class="ml-2"
                      >
                      <template #label>
                        <label v-html="beaconIdLabel"></label>
                      </template>
                    </v-text-field>
                  </v-col>
                </template>
              </v-row>

              <!-- Row 4 (Add Resident Contact Checkbox) ADD MODE ONLY -->
              <template v-if="!isResidentManager && editedResident.id < 0">
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-checkbox
                      v-model="showAddUser"
                      :color="appColor_Selected"
                      >
                      <template v-slot:label>
                        <span class="ml-2" v-html="addContactDefaultText"></span>
                      </template>
                    </v-checkbox>
                  </v-col>
                </v-row>
              </template>

              <!-- Add Contact Inputs -->
              <template v-if="!isResidentManager && showAddUser">
                <!-- Row 5 (FF Email) -->
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedUser.email"
                      :label="getExpression('Label', 'Email')"
                      v-validate="showAddUser ? 'required|email' : ''"
                      :error-messages="errors.collect('email')"
                      data-vv-name="email"
                      @keydown.space.prevent
                      ref="email"
                    />
                  </v-col>
                </v-row>

                <!-- Row 6 (FF Name) -->
                <v-row no-gutters>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="editedUser.name"
                      :label="getExpression('Label', 'Name')"
                      v-validate="showAddUser ? 'required|max:50|alpha_spaces' : ''"
                      :error-messages="errors.collect('name')"
                      data-vv-name="name"
                    />
                  </v-col>
                </v-row>

                <!-- Row 7 (FF Display Name) -->
                <v-row no-gutters>
                  <v-col v-if="isGenie" cols="12" sm="12">
                    <v-text-field
                      v-model="editedUser.nickname"
                      :label="getExpression('Label', 'DisplayName')"
                      v-validate="showAddUser ? 'max:20' : ''"
                      :error-messages="errors.collect('nickname')"
                      data-vv-name="nickname"
                    />
                  </v-col>
                </v-row>
              </template>
            </form>
          </v-container>
        </v-card-text>

        <!-- Edit/Delete -->
        <template v-if="editedResident.id > 0">
          <v-card-actions class="mb-2">
            <v-btn @click="cancel" :loading="isSaving" :disabled="disableCancelResident" class="rounded-0" width="120" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
            <v-btn @click="save" :color="appColor_ResidentDark" dark :loading="isSaving" class="rounded-0 ml-2" width="177" large>{{ saveResidentText }}</v-btn>
          </v-card-actions>

          <template v-if="!isResidentManager">
            <v-divider />

            <v-card-actions>
              <v-btn
                dark
                color="error"
                @click="remove()"
                :loading="isSaving"
                large
                width="305"
                class="mt-2 rounded-0"
                >{{ getExpression('Button', 'DeleteResident') }}
              </v-btn>
            </v-card-actions>
          </template>
        </template>

        <template v-else>
          <v-card-actions>
            <v-spacer />

            <v-btn @click="cancel" large :loading="isSaving" class="rounded-0">{{ getExpression('Button', 'Cancel') }}</v-btn>
            <v-btn @click="save" :color="appColor_ResidentDark" dark large :loading="isSaving" class="rounded-0 ml-4">{{ getExpression('Button', 'Save') }}</v-btn>

          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import AppResident from '@/plugins/resident.js'
import { AppColor, KioskTypeId, ImageUri, LanguageId, UserRole, AddContactContext } from '@/config.js'

export default {
  name: 'AppResident',
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {

      // colors
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // required params
      kioskTypeId: null,
      addContactDefaultText: null,

      // user roles
      role_Guest: UserRole.Guest,
      role_Manager: UserRole.Manager,

      // add contact context
      context_ResidentAdd: AddContactContext.ResidentAdd,

      // optioanal params
      onAdd: {},
      onUpdate: {},
      onCancel: {},
      onDelete: {},
      onChangePicture: {},

      pictureDialog: false,
      isDeleting: false,
      isSaving: false,
      visible: false,

      editedResident: {
        id: -1,
        kioskId: null,
        kioskTypeId: null,
        firstName: null,
        lastName: null,
        bluetoothBeaconId: null,
        kioskKeyTagId: null,
        languageId: null,
        gameDifficultyLevel: null
      },
      defaultResident: {
        id: -1,
        kioskId: null,
        kioskTypeId: null,
        firstName: null,
        lastName: null,
        bluetoothBeaconId: null,
        kioskKeyTagId: null,
        languageId: null,
        gameDifficultyLevel: null
      },
      restoreResident: {
        id: -1,
        firstName: null,
        lastName: null,
        bluetoothBeaconId: null,
        kioskKeyTagId: null,
        languageId: null,
        gameDifficultyLevel: null
      },

      // user add
      showAddUser: false,
      fixedPassword: '@mbient1',
      invitationSnackbar: false,

      editedUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        residentIds: null,
        role: this.role_Guest,
        languageId: null
      },
      defaultUser: {
        id: null,
        name: null,
        email: null,
        nickname: null,
        residentIds: null,
        role: this.role_Guest,
        languageId: null
      }
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'languages',
      'activeKiosk',
      'activeResident',
      'isKioskManager',
      'isResidentManager',
      'kioskKeyNums',
      'locale']),
    formTitle () {
      return this.editedResident.id > 0
        ? this.getExpression('FormTitle', 'EditResident')
        : this.getExpression('FormTitle', 'AddResident')
    },
    languagedLanguages () {
      const self = this
      if (this.isNullOrUndefined(this.languages)) return []

      return this.languages.map(l => { return {
        id: l.id,
        description: self.getExpression('Language', l.description)
      }})
    },
    dictionary () { 
      return {
        attributes: {
          email: 'Email'
        },
        custom: {
          email: {
            email: this.getExpression('Validation', 'InvalidEmail'),
            required: () => this.getExpression('Validation', 'EmailRequired')
          },
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '50'),
            alpha_spaces: this.getExpression('Validation', 'NameAlphaSpaces')
          },
          nickname: {
            max: this.getExpression('Validation', 'DisplayNameMaxChars').replace('{0}', '20')
          },
          firstName: {
            required: () => this.getExpression('Validation', 'FirstNameRequired'),
            max: this.getExpression('Validation', 'FirstNameMaxChars').replace('{0}', '100')
          },
          keyId: {
            required: () => this.getExpression('Validation', 'KeyIdRequired')
          },
          language: {
            required: () => this.getExpression('Validation', 'LanguageRequired')
          }
        }
      }
    },
    isGenie () {
      return this.kioskTypeId === this.kioskTypeId_Genie
    },
    isAbby () {
      return this.kioskTypeId === this.kioskTypeId_Abby
    },
    beaconIdLabel () {
      return this.isAbby
        ? this.getExpression('Label', 'BeaconId')
        : this.getExpression('Label', 'KeyId')
    },
    keyNums () {
      if (this.isNullOrUndefined(this.activeKiosk)) return []
      if (this.isNullOrUndefined(this.kioskKeyNums)) return []

      const self = this
      const selectionNone = {
        id: -1,
        keyNum: self.getExpression('Label', 'None'),
        kioskId: self.activeKiosk.id
      }

      var keyNums = []
      keyNums.push(selectionNone)
      
      const isEditMode = self.editedResident.id > 0

      // unless we are in Edit mode (i.e. from Resident Dashboard) active residentId is irrelevent
      const residentId = isEditMode ? self.activeResident.id : -1

      const filtered = keyNums.concat(self.kioskKeyNums
        .filter(kt => kt.kioskId === self.activeKiosk.id
          && (kt.residentId === null || kt.residentId === residentId)))

      const result = filtered.map(kt => { return {
        id: kt.id,
        keyNum: kt.keyNum
      }})

      return result
    },
    picture () {
      return this.pictureUrl !== null
        ? this.pictureUrl
        : ImageUri.NoAvatar
    },
    pictureUrl () {
      if (this.activeResident === null) return null
      if (this.isNullOrUndefined(this.activeResident.pictureBase64)) return null
      return this.activeResident.pictureBase64
    },
    changePictureText () {
      return this.pictureUrl === null || this.pictureUrl === ImageUri.NoAvatar
        ? this.getExpression('Button', 'AddPicture')
        : this.getExpression('Button', 'ChangePicture')
    },
    saveResidentText () {
      return this.disableCancelResident
        ? this.getExpression('Button', 'Close')
        : this.getExpression('Button', 'SaveChanges')
    },
    disableCancelResident () {
      if (this.editedResident.id <= 0) return true

      const firstName = this.editedResident.firstName.trim()
      const lastName = this.editedResident.lastName !== null
        ? this.editedResident.lastName.trim()
        : null

      // cast bluetoothBeaconId as Int
      var bluetoothBeaconId = null
      if (this.editedResident.bluetoothBeaconId !== null) {
        bluetoothBeaconId = isNaN(this.editedResident.bluetoothBeaconId) 
          ? null 
          : parseInt(this.editedResident.bluetoothBeaconId)
      }
      const kioskKeyTagId = this.editedResident.kioskKeyTagId
      const languageId = this.editedResident.languageId

      const gameDifficultyLevel = this.editedResident.gameDifficultyLevel !== null
        ? this.editedResident.gameDifficultyLevel
        : null

      return firstName === this.restoreResident.firstName &&
        lastName === this.restoreResident.lastName &&
        bluetoothBeaconId === this.restoreResident.bluetoothBeaconId &&
        kioskKeyTagId === this.restoreResident.kioskKeyTagId &&
        languageId === this.restoreResident.languageId &&
        gameDifficultyLevel === this.restoreResident.gameDifficultyLevel
    },
    width () {
      return this.editedResident.id > 0
        ? 320
        : 310
    }
  },
  methods: {
    add (params) {
      this.initParams(params)

      this.editedResident.languageId = !this.isNullOrUndefined(this.activeKiosk.languageId)
        ? this.activeKiosk.languageId
        : LanguageId.English

      this.editedResident.kioskKeyTagId = -1

      this.visible = true
      this.setFocus()
    },

    edit (params) {
      if (this.activeResident === null) return

      this.initParams(params)

      this.editedResident.id = this.activeResident.id
      this.editedResident.firstName = this.activeResident.firstName
      this.editedResident.lastName = this.activeResident.lastName
      this.editedResident.bluetoothBeaconId = this.activeResident.bluetoothBeaconId
      this.editedResident.kioskKeyTagId = this.activeResident.kioskKeyTagId
      if (this.activeResident.kioskKeyTagId === null)
        this.editedResident.kioskKeyTagId = -1
      this.editedResident.languageId = this.activeResident.languageId

      if (this.isAbby) {
        this.editedResident.gameDifficultyLevel = this.activeResident.gameDifficultyLevel
      }

      this.restoreResident = Object.assign({}, this.editedResident)

      this.visible = true
      this.setFocus()
    },

    initParams (params) {
      if (this.isNullOrUndefined(params)) return
      this.kioskTypeId = params.kioskTypeId
      if (!this.isNullOrUndefined(params.addContactText)) this.addContactDefaultText = params.addContactText
      else this.addContactDefaultText = this.getExpression('Label', 'AddOptionalResidentContact')
      if (!this.isNullOrUndefined(params.onAdd)) this.onAdd = params.onAdd
      if (!this.isNullOrUndefined(params.onUpdate)) this.onUpdate = params.onUpdate
      if (!this.isNullOrUndefined(params.onCancel)) this.onCancel = params.onCancel
      if (!this.isNullOrUndefined(params.onDelete)) this.onDelete = params.onDelete
      if (!this.isNullOrUndefined(params.onChangePicture)) this.onChangePicture = params.onChangePicture

      this.$validator.localize('en', this.dictionary)
      this.$validator.reset()
    },

    save () {
      const self = this

      const isAddMode = self.editedResident.id < 0
      const residentModel = self.getModel(self.editedResident, isAddMode)
      const isDataChanged = isAddMode || self.dataChanged(residentModel)

      if (isDataChanged) {
        self.$validator.resume()
        self.$validator.validateAll().then((valid) => {
          if (valid) {
            self.isSaving = true
            if (isAddMode) {
              const userModel = self.showAddUser
                ? self.getUserModel(self.editedUser, residentModel)
                : null

              appService.addResident(residentModel, self.locale).then((data) => {
                if (data.errorSeverity !== null) {
                  self.handleError(data)
                } else {
                  const resident = self.getModel(data.resident)
                  if (self.showAddUser) {
                    userModel.residentIds = [data.resident.id]
                    appService.createUserFromDashboard(userModel, 'resident', self.locale).then(data => {
                      if (data.errorSeverity !== null) {
                        self.handleError(data)
                      } else {
                        self.reset()
                        self.visible = false
                        if (typeof self.onAdd === 'function') {
                          self.onAdd(resident)
                        }
                      }
                      self.isSaving = false
                    })
                  } else {
                    self.visible = false
                    self.reset()
                    if (typeof self.onAdd === 'function') {
                      self.onAdd(resident)
                    }
                  }
                }
                if (!self.showAddUser || data.errorSeverity !== null) self.isSaving = false
              }).catch(e => { self.handleCriticalError(e) })
            } else {
              const updateBaseOnly = true

              appService.updateResident(self.activeResident.id, residentModel, updateBaseOnly, self.locale).then(data => {
                if (data.errorSeverity !== null) {
                  self.handleError(data)
                } else {
                  self.reset()
                  self.visible = false
                  if (typeof self.onUpdate === 'function') {
                    const resident = self.getModel(data.resident)
                    self.onUpdate(resident)
                  }
                }
                self.isSaving = false
              })
            }
          } else {
            self.$validator.pause()
          }
        })
      } else {
        self.reset()
        self.visible = false
      }
    },

    getModel (r, isAddMode) {
      const id = !isAddMode ? r.id : -1

      const firstNameString = r.firstName?.trim() === undefined ? '' : r.firstName?.trim()
      const lastNameString = r.lastName?.trim() === undefined ? '' : r.lastName?.trim() ?? ''
      const nameString = (`${firstNameString} ${lastNameString}`).trim()

      // cast bluetoothBeaconId as Int
      var bluetoothBeaconId = null
      if (r.bluetoothBeaconId !== null) {
        bluetoothBeaconId = isNaN(r.bluetoothBeaconId) ? null : parseInt(r.bluetoothBeaconId)
      }

      const residentKioskId = !this.isNullOrUndefined(this.activeResident)
        ? this.activeResident.kiosk.id
        : -1
        
      const residentKioskTypeId = !this.isNullOrUndefined(this.activeResident)
        ? this.activeResident.kiosk.typeId
        : this.kioskTypeId_Genie

      const kioskTypeId = isAddMode ? this.activeKiosk.typeId : residentKioskTypeId
      const kioskId = isAddMode ? this.activeKiosk.id : residentKioskId
      const updateByUserId = this.profile.id
      const kioskKeyTagId = r.kioskKeyTagId > 0 ? r.kioskKeyTagId : null

      const model = {
        id: id,
        firstName: firstNameString.length > 0 ? firstNameString : null,
        lastName: lastNameString.length > 0 ? lastNameString : null,
        name: nameString.length > 0 ? nameString : null,
        kioskId: kioskId,
        kioskTypeId: kioskTypeId,
        bluetoothBeaconId: bluetoothBeaconId,
        kioskKeyTagId: kioskKeyTagId,
        languageId: r.languageId,

        // abby
        gender: this.isAbby ? 'M' : null,
        gameDifficultyLevel: this.isAbby
          ? r.gameDifficultyLevel ?? 1
          : null,

        updateByUserId: updateByUserId,
      }

      return model
    },

    getUserModel (u, r) {
      const self = this

      u.picture = null
      u.password = self.fixedPassword

      const name = u.name !== null
        ? u.name.trim()
        : null

      const nicknameSafe = u.nickname !== null
        ? u.nickname.length > 0
           ? u.nickname.trim()
           : ''
        : ''

      // derive nickname from full name
      const nickname = (nicknameSafe.length === 0 && name !== null)
        ? name.trim().split(' ')[0].substring(0, 20)
        : nicknameSafe

      // set language same as the resident's
      const languageId = r.languageId

      const role = self.role_Manager

      // return the model
      return {
        id: u.id,
        email: u.email,
        name: name,
        password: u.password,
        nickname: nickname,
        languageId: languageId,
        role: role,
        updateByUserId: self.profile.id,
        addContactContext: self.context_ResidentAdd,
        residentIds: null // will get set after a resident is added
      }
    },

    remove () {
      const self = this
      const resident = self.getModel(self.activeResident)

      const warningText = self.isGenie
          ? 'DeleteGenieResidentWarning'
          : 'DeleteAbbyResidentWarning'

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteThisResident'),
        width: 310,
        text: self.getExpression('Alert', warningText).replace('{0}', resident.name),
        onConfirm: () => {
          self.$validator.pause()
          self.isDeleting = true
          const updateByUserId = self.profile.id.replace('auth0|', '')
          appService.deleteResident(resident.id, updateByUserId).then((data) => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              appService.removeUserResident(resident.id, updateByUserId).then((data) => {
                if (data.errorSeverity > 0) {
                  self.handleError(data)
                } else {
                  self.reset()
                  self.visible = false
                  if (typeof this.onDelete === 'function') {
                    this.onDelete(resident)
                  }
                }
              }).catch(e => { self.handleCriticalError(e) })
            }
            self.isDeleting = false
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    changePicture () {
      this.$refs.picture.click()
    },

    uploadPicture () {
      const self = this

      const file = self.$refs.picture.files[0]
      if (file === undefined) return

      var img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = function() {
        const imgWidth = this.width
        const imgHeight = this.height

        if (imgWidth < 400 || imgHeight < 400) {
          const text = `${self.getExpression('Alert', 'ResolutionTooLow')}` +
            `${self.getExpression('Alert', 'ImageSizeIsOnly')} ${imgWidth} x ${imgHeight}` +
            `${self.getExpression('Alert', 'Pixels')}.`

          self.$error.show({
            title: self.getExpression('FormTitle', 'InspectionFailed'),
            text: text
          })
        } else {
          let formData = new FormData()
          formData.append('file', file)
          formData.append('residentId', self.activeResident.id)
          formData.append('updateByUserId', self.profile.id)

          self.$upload.execute({
            formData: formData,
            url: 'residents/picture',
            fileCount: 1,
            onComplete: (data) => {

              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                const resident = {
                  id: self.activeResident.id,
                  pictureBase64: data.base64
                }
                if (typeof self.onChangePicture === 'function') {
                  self.onChangePicture(resident)
                }
              }
            }
          })
        }
      }
    },

    cancel () {
      const self = this

      if (typeof self.onCancel === 'function') {
        self.onCancel()
        self.visible = false
      } else {
        self.visible = false
      }
      setTimeout(() => { self.reset() }, 300)
    },

    dataChanged (r) {
      var isIdentical = r.firstName === this.activeResident.firstName 
          && r.lastName === this.activeResident.lastName
          && r.bluetoothBeaconId === this.activeResident.bluetoothBeaconId
          && r.languageId === this.activeResident.languageId

      if (this.isAbby && isIdentical) {
        isIdentical = r.gameDifficultyLevel === this.activeResident.gameDifficultyLevel
                  && r.kioskKeyTagId === this.activeResident.kioskKeyTagId
      }

      return !isIdentical
    },

    reset () {
      this.editedResident = Object.assign({}, this.defaultResident)
      this.editedUser = Object.assign({}, this.defaultUser)
      this.showAddUser = false
      this.addContactDefaultText = null
      this.$validator.reset()
      this.$validator.pause()
    },

    setFocus () {
      const self = this
      setTimeout(() => {
        if (!this.isNullOrUndefined(self.$refs.firstName)) {
          self.$refs.firstName.focus()
        }
      }, 500)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  beforeMount() {
    AppResident.EventBus.$on('add', (params) => {
      this.add(params)
    })
    AppResident.EventBus.$on('edit', (params) => {
      this.edit(params)
    })
  },
  mounted() {
    this.$validator.reset()
  }
}
</script>